import React, { useEffect, useRef } from "react";

const StaticSVG = ({ className }) => (
  <svg className={className} viewBox="0 0 1600 900" preserveAspectRatio="none" aria-hidden="true">
    <defs>
      <linearGradient id="tx-static-fill" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="rgba(16,185,129,0.22)" />
        <stop offset="1" stopColor="rgba(16,185,129,0)" />
      </linearGradient>
    </defs>
    {Array.from({ length: 15 }).map((_, i) => (
      <line key={"h" + i} x1="0" x2="1600" y1={i * 60} y2={i * 60} stroke="rgba(255,255,255,0.04)" />
    ))}
    <path
      d="M0,540 L80,520 L160,500 L240,540 L320,510 L400,480 L480,500 L560,470 L640,460 L720,490 L800,450 L880,430 L960,470 L1040,440 L1120,410 L1200,430 L1280,400 L1360,420 L1440,390 L1520,410 L1600,380 L1600,900 L0,900 Z"
      fill="url(#tx-static-fill)"
    />
    <path
      d="M0,540 L80,520 L160,500 L240,540 L320,510 L400,480 L480,500 L560,470 L640,460 L720,490 L800,450 L880,430 L960,470 L1040,440 L1120,410 L1200,430 L1280,400 L1360,420 L1440,390 L1520,410 L1600,380"
      stroke="rgba(16,185,129,0.7)" strokeWidth="2" fill="none"
    />
  </svg>
);

export default function TradingChartBackground({ className = "", events = [], intense = true, symbol = null, price = null }) {
  const canvasRef = useRef(null);
  const eventsRef = useRef([]);
  const seenIdsRef = useRef(new Set());
  const symbolRef = useRef(symbol);
  const priceRef = useRef(price);
  useEffect(() => { symbolRef.current = symbol; }, [symbol]);
  useEffect(() => { if (typeof price === "number" && isFinite(price) && price > 0) priceRef.current = price; }, [price]);

  useEffect(() => {
    for (const e of events || []) {
      if (!e || seenIdsRef.current.has(e.id)) continue;
      seenIdsRef.current.add(e.id);
      eventsRef.current.push({ ...e, life: 0, spawnedAt: performance.now() });
    }
  }, [events]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const prefersReduced = typeof window !== "undefined"
      && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) return;

    const ctx = canvas.getContext("2d");
    let width = 0, height = 0, dpr = window.devicePixelRatio || 1;
    let raf = 0;
    const MAX_CANDLES = 80;
    const CANDLE_W = intense ? 14 : 10;
    let candles = [];
    // Seed the base price from the current symbol so the drift feels like that ticker
    const seedMap = { AAPL: 189, MSFT: 425, NVDA: 875, TSLA: 248, SPY: 512, IBM: 175, GOOGL: 165, AMZN: 178, META: 495, AMD: 165 };
    let price = seedMap[symbolRef.current] || 200;
    const drift = () => (Math.random() - 0.5) * 1.6;
    const priceRange = { min: price * 0.92, max: price * 1.08 };
    const defaultSymbols = ["AAPL", "MSFT", "NVDA", "TSLA", "SPY", "IBM", "AMZN", "META", "GOOG", "AMD"];
    let chips = [];

    for (let i = 0; i < MAX_CANDLES; i++) {
      const open = price;
      const close = open + drift();
      const high = Math.max(open, close) + Math.random() * 1.1;
      const low = Math.min(open, close) - Math.random() * 1.1;
      candles.push({ open, high, low, close });
      price = close;
      priceRange.min = Math.min(priceRange.min, low);
      priceRange.max = Math.max(priceRange.max, high);
    }

    function resize() {
      const rect = canvas.getBoundingClientRect();
      width = rect.width; height = rect.height;
      dpr = window.devicePixelRatio || 1;
      canvas.width = width * dpr; canvas.height = height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    let lastCandleAt = 0, lastChipAt = 0, t0 = performance.now();

    function step(now) {
      const t = now - t0;
      if (now - lastCandleAt > 900) {
        lastCandleAt = now;
        const prev = candles[candles.length - 1];
        const open = prev.close;
        // Gentle mean-reversion pull toward the real symbol price so the ambient chart tracks
        // whatever ticker the user is looking at.
        const anchor = typeof priceRef.current === "number" ? priceRef.current : null;
        const pull = anchor ? (anchor - open) * 0.06 : 0;
        const move = drift() + pull + (Math.random() < 0.05 ? (Math.random() - 0.5) * 6 : 0);
        const close = open + move;
        const high = Math.max(open, close) + Math.random() * 0.9;
        const low = Math.min(open, close) - Math.random() * 0.9;
        candles.push({ open, high, low, close });
        while (candles.length > MAX_CANDLES) candles.shift();
        priceRange.min = priceRange.min * 0.995 + Math.min(...candles.map((c) => c.low)) * 0.005;
        priceRange.max = priceRange.max * 0.995 + Math.max(...candles.map((c) => c.high)) * 0.005;
      }
      if (now - lastChipAt > 1400) {
        lastChipAt = now;
        // 60% chance to use the actively-selected symbol so pop-ins feel synced
        const useActive = symbolRef.current && Math.random() < 0.6;
        const sym = useActive ? symbolRef.current : defaultSymbols[Math.floor(Math.random() * defaultSymbols.length)];
        const up = Math.random() > 0.45;
        chips.push({ sym, up, pct: (Math.random() * 1.6 + 0.05).toFixed(2),
          x: 40 + Math.random() * Math.max(0, width - 160), y: height, life: 0 });
      }

      ctx.clearRect(0, 0, width, height);
      ctx.strokeStyle = "rgba(255,255,255,0.04)";
      ctx.lineWidth = 1;
      for (let y = 0; y < height; y += 60) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(width, y); ctx.stroke(); }
      for (let x = 0; x < width; x += 60) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, height); ctx.stroke(); }

      const range = Math.max(1, priceRange.max - priceRange.min);
      const chartTop = height * 0.18, chartH = height * 0.62;
      const yFor = (p) => chartTop + (1 - (p - priceRange.min) / range) * chartH;

      ctx.beginPath();
      candles.forEach((c, i) => {
        const x = (i / (MAX_CANDLES - 1)) * width;
        const y = yFor(c.close);
        if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      });
      ctx.lineTo(width, height); ctx.lineTo(0, height); ctx.closePath();
      const grad = ctx.createLinearGradient(0, chartTop, 0, height);
      grad.addColorStop(0, `rgba(16,185,129,${intense ? 0.15 : 0.08})`);
      grad.addColorStop(1, "rgba(16,185,129,0)");
      ctx.fillStyle = grad; ctx.fill();

      const maWindow = 12;
      ctx.beginPath();
      candles.forEach((c, i) => {
        if (i < maWindow - 1) return;
        const slice = candles.slice(i - maWindow + 1, i + 1);
        const avg = slice.reduce((s, k) => s + k.close, 0) / maWindow;
        const x = (i / (MAX_CANDLES - 1)) * width;
        const y = yFor(avg);
        if (i === maWindow - 1) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      });
      ctx.strokeStyle = `rgba(129,140,248,${intense ? 0.55 : 0.3})`;
      ctx.lineWidth = 1.4; ctx.stroke();

      const stepX = width / MAX_CANDLES;
      const cAlpha = intense ? 0.85 : 0.4;
      candles.forEach((c, i) => {
        const cx = i * stepX + stepX / 2;
        const up = c.close >= c.open;
        const color = up ? `rgba(16,185,129,${cAlpha})` : `rgba(244,63,94,${cAlpha})`;
        ctx.strokeStyle = color; ctx.lineWidth = 1;
        ctx.beginPath(); ctx.moveTo(cx, yFor(c.high)); ctx.lineTo(cx, yFor(c.low)); ctx.stroke();
        const bt = yFor(Math.max(c.open, c.close));
        const bb = yFor(Math.min(c.open, c.close));
        const bh = Math.max(1, bb - bt);
        ctx.fillStyle = color;
        ctx.fillRect(cx - Math.min(CANDLE_W, stepX * 0.6) / 2, bt, Math.min(CANDLE_W, stepX * 0.6), bh);
      });

      const sx = ((t / 12) % width);
      const sg = ctx.createLinearGradient(sx - 80, 0, sx + 80, 0);
      sg.addColorStop(0, "rgba(16,185,129,0)");
      sg.addColorStop(0.5, `rgba(16,185,129,${intense ? 0.35 : 0.18})`);
      sg.addColorStop(1, "rgba(16,185,129,0)");
      ctx.fillStyle = sg; ctx.fillRect(sx - 80, 0, 160, height);

      chips = chips.filter((chip) => chip.life < 6000);
      chips.forEach((chip) => {
        chip.life += 16.66;
        const a = Math.max(0, Math.min(0.55, 0.55 * (1 - chip.life / 6000)));
        const y = chip.y - (chip.life / 6000) * (height * 0.35);
        ctx.font = "600 11px 'JetBrains Mono', ui-monospace, monospace";
        ctx.fillStyle = `rgba(226,232,240,${a})`;
        ctx.fillText(chip.sym, chip.x, y);
        ctx.fillStyle = chip.up ? `rgba(16,185,129,${a + 0.15})` : `rgba(244,63,94,${a + 0.15})`;
        ctx.fillText(`${chip.up ? "▲" : "▼"} ${chip.pct}%`, chip.x + 56, y);
      });

      // Real trade pop-ins (larger, brighter)
      eventsRef.current = eventsRef.current.filter((e) => e.life < 5500);
      eventsRef.current.forEach((e) => {
        e.life += 16.66;
        const p = e.life / 5500;
        const alpha = p < 0.15 ? p / 0.15 : (1 - (p - 0.15) / 0.85);
        const scale = 0.6 + 0.6 * Math.min(1, p * 3);
        const cx = width / 2, cy = height * 0.42;
        ctx.save();
        ctx.globalAlpha = Math.max(0, alpha);
        ctx.translate(cx, cy);
        ctx.scale(scale, scale);
        const up = (e.pnl ?? 0) >= 0;
        const bg = up ? "rgba(16,185,129,0.18)" : "rgba(244,63,94,0.18)";
        const bd = up ? "rgba(16,185,129,0.8)" : "rgba(244,63,94,0.8)";
        ctx.fillStyle = bg; ctx.strokeStyle = bd; ctx.lineWidth = 2;
        const w = 340, h = 96, r = 16;
        ctx.beginPath();
        ctx.moveTo(-w/2 + r, -h/2);
        ctx.arcTo(w/2, -h/2, w/2, h/2, r);
        ctx.arcTo(w/2, h/2, -w/2, h/2, r);
        ctx.arcTo(-w/2, h/2, -w/2, -h/2, r);
        ctx.arcTo(-w/2, -h/2, w/2, -h/2, r);
        ctx.closePath();
        ctx.fill(); ctx.stroke();
        ctx.font = "700 18px 'Syne', 'Manrope', sans-serif";
        ctx.fillStyle = "rgba(226,232,240,0.95)";
        ctx.textAlign = "center";
        ctx.fillText(`${e.side || (up ? "BUY" : "SELL")} · ${e.symbol}`, 0, -12);
        ctx.font = "600 20px 'JetBrains Mono', ui-monospace, monospace";
        ctx.fillStyle = up ? "rgba(52,211,153,1)" : "rgba(251,113,133,1)";
        ctx.fillText(`${up ? "+" : ""}$${Number(e.pnl || 0).toFixed(2)}`, 0, 22);
        ctx.restore();
      });

      raf = requestAnimationFrame(step);
    }
    raf = requestAnimationFrame(step);
    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
  }, [intense]);

  const prefersReduced = typeof window !== "undefined"
    && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  if (prefersReduced) return <StaticSVG className={`pointer-events-none select-none ${className}`} />;
  return <canvas ref={canvasRef} aria-hidden="true" className={`pointer-events-none select-none ${className}`} />;
}
